import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserPlus, CheckCircle2 } from "lucide-react";
import api, { apiError } from "../lib/api";

const ERROR_KEYS: Record<string, string> = {
  INVALID_EMAIL: "register.errInvalidEmail",
  NAME_REQUIRED: "register.errName",
  PASSWORD_TOO_SHORT: "register.errShort",
  PASSWORD_MISMATCH: "register.errMismatch",
  EMAIL_ALREADY_REGISTERED: "register.errExists",
};

export default function Register() {
  const { t, i18n } = useTranslation();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", password_confirm: "", locale: i18n.language === "fr" ? "fr" : "en" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (f.password !== f.password_confirm) { setError(t("register.errMismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/register", f);
      setDone(true);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" && ERROR_KEYS[detail] ? t(ERROR_KEYS[detail]) : apiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md fade-up">
        <div className="swiss-card p-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#C8A44D]">Vytali Academy</p>
          <h1 className="font-heading text-2xl font-semibold mt-2">{t("register.title")}</h1>
          {done ? (
            <div className="mt-5" data-testid="register-success">
              <div className="flex items-start gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-3">
                <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                <p className="text-sm">{t("register.success")}</p>
              </div>
              <Link to="/login" data-testid="register-to-login" className="mt-4 inline-block text-sm text-[#002BF6] hover:underline">{t("login.backToLogin")}</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-3.5" data-testid="register-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("register.firstName")}</label>
                  <input data-testid="register-first-name" required value={f.first_name} onChange={set("first_name")}
                    className="w-full px-3 py-2.5 border border-border rounded-md bg-white text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("register.lastName")}</label>
                  <input data-testid="register-last-name" required value={f.last_name} onChange={set("last_name")}
                    className="w-full px-3 py-2.5 border border-border rounded-md bg-white text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("login.email")}</label>
                <input data-testid="register-email" type="email" required value={f.email} onChange={set("email")}
                  className="w-full px-3 py-2.5 border border-border rounded-md bg-white text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("register.phone")}</label>
                <input data-testid="register-phone" value={f.phone} onChange={set("phone")}
                  className="w-full px-3 py-2.5 border border-border rounded-md bg-white text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("login.password")}</label>
                  <input data-testid="register-password" type="password" required minLength={8} value={f.password} onChange={set("password")}
                    className="w-full px-3 py-2.5 border border-border rounded-md bg-white text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("register.confirm")}</label>
                  <input data-testid="register-password-confirm" type="password" required minLength={8} value={f.password_confirm} onChange={set("password_confirm")}
                    className="w-full px-3 py-2.5 border border-border rounded-md bg-white text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("register.language")}</label>
                <select data-testid="register-locale" value={f.locale} onChange={set("locale")}
                  className="w-full px-3 py-2.5 border border-border rounded-md bg-white text-sm">
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </div>
              {error && <p data-testid="register-error" className="text-sm text-[#FF3B30] font-medium">{error}</p>}
              <button data-testid="register-submit" type="submit" disabled={loading}
                className="w-full py-2.5 bg-[#002BF6] text-white font-medium rounded-md hover:bg-[#0024cc] transition-colors duration-200 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                <UserPlus size={16} /> {loading ? "…" : t("register.submit")}
              </button>
              <p className="text-xs text-muted-foreground">{t("register.approvalNotice")}</p>
              <Link to="/login" data-testid="register-login-link" className="block text-center text-sm text-[#002BF6] hover:underline">{t("register.haveAccount")}</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
